import { useSelector } from "react-redux";
import { useReactFlow, getOutgoers } from "reactflow";
import { NODE_TYPES, EDGE_TYPES } from "constants/flowDesigner";
import { tryCatchWrapper } from "helpers/common";

const sortNodes = (nodes, isVerticalCanvas) => {
  nodes.sort((a, b) => {
    const aPos = a.position;
    const bPos = b.position;

    if (isVerticalCanvas) {
      if (aPos.y === bPos.y) {
        return aPos.x - bPos.x;
      }
      return aPos.y - bPos.y;
    }

    if (aPos.x === bPos.x) {
      return aPos.y - bPos.y;
    }
    return aPos.x - bPos.x;
  });
};

const useGetBranchNodes = () => {
  const { isVerticalCanvasDirection } = useSelector((store) => store?.designer?.settings);
  const { getNodes, getEdges } = useReactFlow();

  const getNodeOutgoers = (node, outgoerNodes, edgesWithoutFloating, allNodes) => {
    const outgoers = getOutgoers(node, allNodes, edgesWithoutFloating);

    if (outgoers.length === 0) {
      return;
    }

    outgoerNodes.push(...outgoers);

    outgoers.forEach((outgoer) => {
      getNodeOutgoers(outgoer, outgoerNodes, edgesWithoutFloating, allNodes);
    });
  };

  const getUniqueNodesAndMergeNodeId = (splitNode) => {
    const outgoerNodes = [];
    let mergeNodeId = splitNode?.data?.mergeId;

    const allNodes = getNodes();
    const edgesWithoutFloatingAndStraight = getEdges().filter(
      (edge) => edge.type !== EDGE_TYPES.FLOATING && edge.type !== EDGE_TYPES.STRAIGHT
    );

    tryCatchWrapper(getNodeOutgoers)(
      splitNode,
      outgoerNodes,
      edgesWithoutFloatingAndStraight,
      allNodes
    );

    sortNodes(outgoerNodes, isVerticalCanvasDirection);

    const seenIds = new Set();
    const childNodeIds = [];

    for (const outgoerNode of outgoerNodes) {
      if (!seenIds.has(outgoerNode.id)) {
        seenIds.add(outgoerNode.id);
        childNodeIds.push(outgoerNode.id);
      } else if (outgoerNode.id === mergeNodeId) {
        break;
      }
    }

    if (!mergeNodeId) {
      const mergeNode = outgoerNodes.find((node) => !node.data?.isBranch);
      mergeNodeId = mergeNode?.id;
    }

    return { mergeNodeId, childNodeIds };
  };

  const getMergeNodeId = (node) => {
    const { mergeNodeId } = tryCatchWrapper(getUniqueNodesAndMergeNodeId)(node);
    return mergeNodeId;
  };

  const getChildNodeIdsBeforeMergeNode = (conditionalNode) => {
    if (!conditionalNode) return [];

    let { childNodeIds } = tryCatchWrapper(getUniqueNodesAndMergeNodeId)(
      conditionalNode
    );

    const nodesMap = new Map(getNodes().map((node) => [node.id, node]));

    childNodeIds = childNodeIds.filter((childNodeId) => {
      const node = nodesMap.get(childNodeId);
      return node?.data?.isBranch || node?.type === NODE_TYPES.HIDDEN;
    });

    return childNodeIds;
  };

  return { getMergeNodeId, getChildNodeIdsBeforeMergeNode };
};

export default useGetBranchNodes;
